import { SECTIONS, type Section } from '../../features/routing/constants';
import { parsePathname } from '../../features/routing/utils';

export type Direction = 'left' | 'right' | 'fade';

export const SECTIONS_HIERARCHY: Section[] = [
  SECTIONS.Welcome,
  SECTIONS.Demo,
  SECTIONS.Login,
  SECTIONS.Home,
  SECTIONS.Mood_log_entry,
  SECTIONS.Patches,
  SECTIONS.Profile,
  SECTIONS.Unknown,
] as const;

/**
 * Get the transition direction based on the current and next pathnames.
 * All of the pagetransitions in the app are defined here, either manually or algorithmically.
 * @param currentPathname - The current pathname.
 * @param nextPathname - The next pathname.
 * @returns The transition direction.
 */
export const getTransitionDirection = (
  currentPathname: string,
  nextPathname: string
): Direction => {
  const currentPathInfo = parsePathname(currentPathname);
  const nextPathInfo = parsePathname(nextPathname);

  const isSameSection = currentPathInfo.section === nextPathInfo.section;

  if (isSameSection) {
    // If the sections are the same and both have steps, determine the direction based on the step number.
    if (currentPathInfo.stepNumber !== null && nextPathInfo.stepNumber !== null) {
      if (currentPathInfo.stepNumber === nextPathInfo.stepNumber) {
        return 'fade';
      }
      return currentPathInfo.stepNumber < nextPathInfo.stepNumber ? 'left' : 'right';
    }

    if (currentPathInfo.section === SECTIONS.Home) {
      // We transition left and right in the same order between the home calender and log.
      if (currentPathInfo.segments[1] === 'calendar') {
        return 'left';
      } else if (currentPathInfo.segments[1] === 'log') {
        return 'right';
      }
    }
    return 'fade';
  }

  const currentHierarchy = SECTIONS_HIERARCHY.indexOf(currentPathInfo.section || SECTIONS.Unknown);
  const nextHierarchy = SECTIONS_HIERARCHY.indexOf(nextPathInfo.section || SECTIONS.Unknown);

  if (currentHierarchy < nextHierarchy) {
    return 'left';
  } else if (currentHierarchy > nextHierarchy) {
    return 'right';
  }

  return 'fade';
};
